import { PriceInfo } from '@unpispas/upp-defs';

import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from '../base';
import { CreateObject } from '../item';
import { PayAccount } from './payaccount';
import { AccountInvoiceView, AccountInvoiceViewProxy } from '../views/accountinvoice';

import { Ticket } from './ticket';
import { Ticketbai } from './ticketbai';
import { Ticketsii } from './ticketsii';
import { Verifactu } from './verifactu';

import { InvoiceType, PlaceView, SerialType } from '../views/place';

import { PayAccountType } from '../types';
import { TicketbaiType } from '../types';
import { TicketsiiType } from '../types';
import { VerifactuType } from '../types';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

const _schema = {
    name: 'ACCOUNTINVOICE',
    fields: [
        { name: 'created', type: 'date', volatile: true },
        { name: 'updated', type: 'date', volatile: true },
        { name: 'account', type: 'objid' },
        { name: 'since', type: 'date' },
        { name: 'until', type: 'date' },
        { name: 'series', type: 'string', default: 'S00000000' },
        { name: 'invoice', type: 'string' },
        { name: 'amount', type: 'number' },
        { name: 'payment', type: 'string' },
    ],
    relate: [
        { direction: '>', target: 'PAYACCOUNT', by: 'account', name: 'account', reason: 'account', child: false, class: PayAccountType },
        { direction: '<', target: 'TICKETBAI', by: 'invceac', name: 'ticketbai', reason: 'ticketbais', child: true, class: TicketbaiType },
        { direction: '<', target: 'VERIFACTU', by: 'invceac', name: 'verifactu', reason: 'verifactus', child: true, class: VerifactuType },
        { direction: '<', target: 'TICKETSII', by: 'invceac', name: 'ticketsii', reason: 'ticketsiis', child: true, class: TicketsiiType },
    ]
} as const;

const _AccountInvoiceClass = CreateObject(_schema);
if (!_AccountInvoiceClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class AccountInvoice extends _AccountInvoiceClass {
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): AccountInvoice {
        return this._Copy(store) as AccountInvoice;
    }

    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf){
            return;
        }

        if (relation === 'account' && related instanceof PayAccount){
            if (related === this.account){
                related.AddInvoice(this);
            }
            else {
                related.DelInvoice(this);
            }
        }
    }

    override get viewProxy(): AccountInvoiceView {
        return AccountInvoiceViewProxy(this, this.data);
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get payment(): string | null {
        const _payment = this['__base__payment'] as string | null;

        switch (_payment){
            case 'CASHPAY':
                return 'PAYCASH';
            case 'CARDPAY':
                return 'PAYCARD';
        }

        if (_payment == null){
            return 'UNKNOWN';
        }

        return _payment;
    }

    set payment(value: string | null){
        if (this['__base__payment'] === value){
            return;
        }

        this['__base__payment'] = value;
        this.ToUpdate = true;
    }

    get place(){
        return this.account?.place ?? null;
    }

    get total(): number | null {
        return this.amount;
    }

    get tickets(): Ticket[] {
        return (this._chldlist['tickets'] as Ticket[]) || [];
    }

    get ticketbai(): Ticketbai | null {
        const _ticketbai = this._chldlist['ticketbais'] || [];
        if (_ticketbai.length > 0){
            return _ticketbai[0] as Ticketbai;
        }

        return null;
    }

    get verifactu(): Verifactu | null {
        const _verifactu = this._chldlist['verifactus'] || [];
        if (_verifactu.length > 0){
            return _verifactu[0] as Verifactu;
        }

        return null;
    }

    get ticketsii(): Ticketsii | null {
        const _ticketsii = this._chldlist['ticketsiis'] || [];
        if (_ticketsii.length > 0){
            return _ticketsii[0] as Ticketsii;
        }

        return null;
    }

    get SplitTaxes(): PriceInfo {
        const _combined = new PriceInfo();

        for (const _ticket of this.tickets){
            const _taxes = _ticket.ticketdata?.Registry?.commercial?.totals?.taxes || [];

            for (const _tax of _taxes){
                if (_tax.total === 0){
                    continue;
                }

                _combined.Add(_tax.total, _tax.rate);
            }
        }

        return _combined;
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    AddTicket(child: Ticket): void {
        this.AddChild('tickets', child, 'place');
    }

    DelTicket(child: Ticket): void {
        this.DelChild('tickets', child, 'place');
    }

    async SetInvoiceNumber(): Promise<void> {
        const _placeobj = this.account?.place;
        if (!_placeobj){
            console.error('[ACCOUNTINVOICE] Cannot assign invoice number without place.');
            return;
        }

        const _place = this.data.alives.get(_placeobj) as PlaceView | null;
        if (!_place){
            console.error('[ACCOUNTINVOICE] Place view is not available for invoice numbering.');
            return;
        }

        const [_serial, _invoice] = await _place.NextDeviceTicket(SerialType.A, InvoiceType.A);
        if (!_serial || !_invoice){
            return;
        }

        this.series = _serial;
        this.invoice = _invoice;
        this.created = new Date();
        this.updated = new Date();
        this.ToUpdate = true;
        this.DoRefresh('ACCOUNTINVOICE');
    }

    async AddTicketBAI(): Promise<Ticketbai | null> {
        if (this.tickets.length === 0){
            console.error('[ACCOUNTINVOICE] This account invoice is empty!');
            return null;
        }

        const _place = this.place;
        if (!_place?.SendBAIEnabled){
            return null;
        }

        if (!this.invoice){
            console.error('[ACCOUNTINVOICE] Cannot generate TicketBAI without invoice number.');
            return null;
        }

        if (this.ticketbai){
            console.warn('[ACCOUNTINVOICE] overwriting ticketbai instance will have no effect');
            return this.ticketbai;
        }

        const _ticketbai = new Ticketbai(null, this.data);
        if (_ticketbai){
            _ticketbai.invoice = this;
            await _ticketbai.Refresh();
            if (_ticketbai.xml){
                this.AddTicketbai(_ticketbai);
            }    
        }

        return _ticketbai;
    }

    async AddVeriFactu(): Promise<Verifactu | null> {
        if (this.tickets.length === 0){
            console.error('[ACCOUNTINVOICE] This account invoice is empty!');
            return null;
        }

        const _place = this.place;
        if (!_place?.SendVFTEnabled){
            return null;
        }

        if (!this.invoice){
            console.error('[ACCOUNTINVOICE] Cannot generate VeriFactu without invoice number.');
            return null;
        }

        if (this.verifactu){
            console.warn('[ACCOUNTINVOICE] overwriting verifactu instance will have no effect');
            return this.verifactu;
        }

        const _verifactu = new Verifactu(null, this.data);
        if (_verifactu){
            _verifactu.invoice = this;
            if (await _verifactu.Refresh()){
                this.AddVerifactu(_verifactu);
            }
        }

        return _verifactu;
    }
}
